import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type PartyThemeId = "midnight" | "desert" | "neon";
export type PartyTypeId = "rubik" | "kufi" | "tajawal";

export interface PartyTweaksState {
  theme: PartyThemeId;
  type: PartyTypeId;
  motion: number;
}

const defaultTweaks: PartyTweaksState = {
  theme: "midnight",
  type: "rubik",
  motion: 1,
};

interface PartyShellContextValue {
  tweaks: PartyTweaksState;
  setTweaks: React.Dispatch<React.SetStateAction<PartyTweaksState>>;
  tweaksOpen: boolean;
  setTweaksOpen: React.Dispatch<React.SetStateAction<boolean>>;
  toast: string | null;
  showToast: (msg: string, ms?: number) => void;
}

const PartyShellContext = createContext<PartyShellContextValue | null>(null);

export function PartyShellProvider({ children }: { children: React.ReactNode }) {
  const [tweaks, setTweaks] = useState<PartyTweaksState>(() => {
    try {
      const raw = localStorage.getItem("kan.party.tweaks");
      if (raw) return { ...defaultTweaks, ...JSON.parse(raw) };
    } catch {
      /* ignore */
    }
    return defaultTweaks;
  });
  const [tweaksOpen, setTweaksOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem("kan.party.tweaks", JSON.stringify(tweaks));
    } catch {
      /* ignore */
    }
  }, [tweaks]);

  const showToast = useCallback((msg: string, ms = 2000) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), ms);
  }, []);

  const value = useMemo(
    () => ({
      tweaks,
      setTweaks,
      tweaksOpen,
      setTweaksOpen,
      toast,
      showToast,
    }),
    [tweaks, tweaksOpen, toast, showToast]
  );

  return (
    <PartyShellContext.Provider value={value}>
      {children}
    </PartyShellContext.Provider>
  );
}

export function usePartyShell() {
  const ctx = useContext(PartyShellContext);
  if (!ctx) {
    throw new Error("usePartyShell must be used within PartyShellProvider");
  }
  return ctx;
}
